/*
 *   File : external-investments.js
 *   Author : https://evoqins.com
 *   Description : External Investments
 *   Version : 1.0.0
*/
// packages
import { Fragment, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

// components
import { CustomTabBar } from "../../../../Components/Tab";
import { EmptyScreen } from "../../../../Components/Others";
import { DoughnutChart, HorizontalStackedBar } from "../../../../Components/Chart";
import { DataTableContainer } from "../../../../Components/DataTable";
import { DownloadButton, LinkButton } from "../../../../Components/Buttons";

// services
import APIService from "../../../../Services/api-service";

// styles
import Colors from '../../../../Styles/color.module.scss'


const INVESTMENT_TYPES = [
    {
        label: "Overview",
        id: 1
    },
    {
        label: "Categories",
        id: 2
    },
    {
        label: "Funds",
        id: 3
    },
];

// Table custom style
const TABLE_STYLE = {
    table: {
        style: {
            borderRadius: "0px",
        },
    },

    headRow: {
        style: {
            borderBottomColor: 'transparent',
            paddingLeft: "32px",
            color: Colors.dark_gray,
            fontFamily: "Montserrat-Bold",
            fontSize: "14px",
            lineHeight: "24px",
            paddingTop: "0px",
            paddingBottom: '0px',

        }
    },
    rows: {
        style: {
            paddingLeft: "32px",
            borderBottom: 'none !important',

            '&:nth-child(odd)': {
                backgroundColor: '#0D0D0D05',
            },
        },
    },
    cells: {
        style: {
            paddingTop: '16px',
            paddingBottom: '16px',
            color: Colors.dark_charcoal,
            fontFamily: "Montserrat-Medium",
            fontSize: "14px",
            lineHeight: "24px",
            paddingLeft: "0px",
            userSelect: "none",
        }
    },
    headCells: {
        style: {
            paddingLeft: "0px"
        }
    },
    pagination: {
        style: {
            paddingRight: "50px",
            paddingTop: "10px",
            borderTop: "none"
        }
    }
};

const FILTER_OPTIONS = [
    {
        label: "Live Folio",
        id: 2
    },
    {
        label: "Zero Balance",
        id: 1
    },
]

const PORTFOLIO_OPTIONS = [
    {
        label: "Internal",
        id: 1
    },
    {
        label: "External",
        id: 2
    },
]

const ExternalInvestments = (props) => {
    const holdingsRef = useRef(null);
    const requestIdRef = useRef(0);
    const requestIdRef1 = useRef(0);
    const requestIdRef2 = useRef(0);
    const [loader, setLoader] = useState(true);
    const [summaryData, setSummaryData] = useState({});
    const [investmentType, setInvestmentType] = useState(INVESTMENT_TYPES[0].id);
    const [fundAllocationData, setFundAllocationData] = useState([]);
    const [categoryAllocationData, setCategoryAllocationData] = useState([]);
    const [externalData, setExternalData] = useState({});
    const [pageNumber, setPageNumber] = useState(1);
    const [selected, setSelected] = useState(FILTER_OPTIONS[0].id);
    const [listLoader, setListLoader] = useState(true);
    const [portfolioType, setPortfolioType] = useState(PORTFOLIO_OPTIONS[0].id);
    const [downloadLoader, setDownloadLoader] = useState(false);
    const [holdingsOverview, setHoldingsOverView] = useState({});
    const [showAllHoldings, setShowAllHoldings] = useState(false);
    const [filterLoader, setFilterLoader] = useState(true);
    const [overviewOptions, setOverviewOptions] = useState([]);
    const [holdingOptions, setHoldingOptions] = useState([]);
    const [overviewType, setOverviewType] = useState(null);
    const [holdingType, setHoldingType] = useState(null);

    const [graphData, setGraphData] = useState({});
    const [holdingsList, setHoldingsList] = useState([]);

    useEffect(() => {
        _getFilterData();
        // eslint-disable-next-line
    }, [])


    useEffect(() => {
        _getPortfolioDetails();
        // eslint-disable-next-line
    }, [portfolioType]);

    useEffect(() => {
        if (overviewType !== null) {
            _getSummary();
        }
        // eslint-disable-next-line
    }, [portfolioType, overviewType]);

    useEffect(() => {
        _getHoldings();
        // eslint-disable-next-line
    }, [portfolioType, holdingType]);

    useEffect(() => {
        _getExternalList();

        // eslint-disable-next-line
    }, [pageNumber, selected])

    useEffect(() => {
        _getExternalList();

        // eslint-disable-next-line
    }, [pageNumber, selected])

    function _handleHoldings(tab_index) {
        setHoldingType(tab_index);
    }

    function _handleOverview(tab_index) {
        setOverviewType(tab_index);
    }

    function _handleInvestmentType(tab_index) {
        setInvestmentType(tab_index);
    }

    function _handleFilter(tab_index) {
        setSelected(tab_index);
    }

    const _handlePageChange = (page) => {
        setPageNumber(page);
    };

    function _handlePortfolioType(tab_index) {
        setPortfolioType(tab_index);
    }

    function _handleShowOverview() {
        setShowAllHoldings(!showAllHoldings);
        if (showAllHoldings === true) {
            holdingsRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    }


    // API - external summary
    const _getPortfolioDetails = () => {
        setLoader(true);
        const url = "/user/portfolio/get";
        const request = {
            customer_id: props.id,
            is_external: portfolioType === 1 ? false : true
        }
        const currentRequestId = ++requestIdRef.current;
        APIService(true, url, request).then((response) => {
            if (currentRequestId === requestIdRef.current) {
                if (response.status_code === 200) {
                    setSummaryData(response.data);
                    const FUND_ALLOCATION_DATA = response.data.funds.graph_data !== undefined && response.data.funds.graph_data.map((item) => {
                        return (
                            {
                                name: item.asset_class,
                                ...item
                            }
                        )
                    })

                    const CATEGORY_ALLOCATION_DATA = response.data.categories.graph_data !== undefined && response.data.categories.graph_data.map((item) => {
                        return (
                            {
                                name: item.asset_class,
                                ...item
                            }
                        )
                    })
                    setFundAllocationData(FUND_ALLOCATION_DATA);
                    setCategoryAllocationData(CATEGORY_ALLOCATION_DATA);
                }
                else {
                    setSummaryData({});
                }
                setLoader(false);
            }
        });
    }

    // API - holding list
    const _getHoldings = () => {
        const url = "/user/portfolio/list";

        const request = {
            customer_id: props.id,
            investment_type: holdingType,
            is_external: portfolioType === 1 ? false : true
        }
        const currentRequestId = ++requestIdRef1.current;
        APIService(true, url, request).then((response) => {
            if (currentRequestId === requestIdRef1.current) {
                if (response.status_code === 200) {
                    setHoldingsList(response.data);
                } else {
                    setHoldingsList([]);
                }
            }

        })
    }


    // API - external summary
    const _getSummary = () => {
        const url = "/user/portfolio/summary";
        const request = {
            customer_id: props.id,
            is_external: portfolioType === 1 ? false : true,
            investment_type: overviewType
        }
        const currentRequestId = ++requestIdRef2.current;
        APIService(true, url, request).then((response) => {
            if (currentRequestId === requestIdRef2.current) {
                if (response.status_code === 200) {
                    setHoldingsOverView(response.data);
                    const labels = response.data.allocation_data.map((item) => item.name)
                    const datasets = response.data.allocation_data.map((item) => item.allocation)
                    const colors = response.data.allocation_data.map((item) => item.color)
                    setGraphData({ labels, datasets, colors })
                }
            }
            else {
                setHoldingsOverView({});
            }
        });

    }

    // API - External List
    const _getExternalList = () => {
        const url = "/user/list-external-portfolio";
        const request = {
            customer_id: props.id,
            "page_num": pageNumber,
            "page_size": 15,
            "filter": selected
        }
        APIService(true, url, request).then((response) => {
            if (response.status_code === 200) {
                setExternalData(response.data);
            } else {
                setExternalData({});
            }
            setListLoader(false);
        })
    }

    const _downloadHolidngs = () => {
        setDownloadLoader(true);

        const url = "/user/external-holdings-report";
        const request = {
            "customer_id": props.id
        }
        APIService(true, url, request).then((response) => {
            toast.dismiss();
            if (response.status_code === 200) {
                toast.success("List downloaded successfully", {
                    type: "success"
                });
                window.open(response.data.url, "_blank");

            } else {
                toast.error(response.message, {
                    type: "error"
                });
            }
            setDownloadLoader(false);
        });
    }

    // API- get filter
    const _getFilterData = () => {
        const url = "/user/portfolio/filter-data";

        const request = {
            "customer_id": props.id
        }

        APIService(true, url, request).then((response) => {
            if (response.status_code === 200) {
                let investment_types = portfolioType === 1 ? response.data.summary_investment_types : response.data.external_summary_investment_types
                if (investment_types !== null) {
                    setOverviewOptions(investment_types);
                    if (investment_types.length > 0) {
                        setOverviewType(investment_types[0].id);
                    }

                } else {
                    setOverviewOptions([]);
                }

                let holding_types = portfolioType === 1 ? response.data.holdings_investment_types : response.data.external_holdings_investment_types
                if (holding_types !== null) {


                    setHoldingOptions(holding_types);

                    setHoldingType(holding_types[0].id);

                } else {
                    setHoldingOptions([]);
                }
            }
            setFilterLoader(false);
        });
    }


    const HOLDINGS_COLUMNS = [
        {
            name: "Folio No.",
            selector: row => row.folio_no === null ? "-" : row.folio_no,
            width: '140px'
        },
        {
            name: "Fund name",
            cell: row => row.fund_name === null ? "-" : row.fund_name,
            width: '200px'
        },
        {
            name: "Category",
            cell: row => row.category === null ? "-" : row.category,
            width: '200px'
        },
        {
            name: "Current amount",
            cell: row => row.current_amount === null ? "-" : row.current_amount,
            width: "160px"
        },
        {
            name: "Invested amount",
            cell: row => row.invested_amount === null ? "-" : row.invested_amount,
            width: "160px"
        },
        {
            name: "Monthly SIP",
            cell: row => row.monthly_sip === null ? "-" : row.monthly_sip,
            width: "160px"
        },
        {
            name: "ARN Code",
            selector: row => row.arn_code === null ? "-" : row.arn_code,
            width: '120px'
        },
        {
            name: "Returns",
            cell: row => <span className={`${row.return > 0 ? "color-emerald-green" : row.return < 0 ? "color-deep-carmine-pink" : "color-eerie-black"}`}>
                {row.return_text}
            </span>,
            width: '160px'
        },
        {
            name: "Purchase date",
            selector: row => row.purchase_date === null ? "-" : row.purchase_date,
            width: '140px'
        },
        {
            name: "Purchase NAV",
            selector: row => row.purchase_nav === null ? "-" : row.purchase_nav,
            width: '140px'
        },
        {
            name: "Units",
            selector: row => row.units === null ? "-" : row.units,
            width: '100px'
        },
        {
            name: "XIRR",
            selector: row => row.xirr === null ? "-" : row.xirr,
            width: '100px'
        },
    ]
    return (
        <Fragment>
            <div className="bg-white border-radius-24px padding-32px-tb mt-4">
                <div className="padding-32px-lr border-bottom-1px border-color-mint-gray mb-3">
                    <div className="row">
                        <div className="col-8">
                            <CustomTabBar data={PORTFOLIO_OPTIONS}
                                selectedTab={portfolioType}
                                onSelectTab={_handlePortfolioType} />
                        </div>
                        {portfolioType === 2 && <div className="col-4 text-end">
                            <DownloadButton label="Download External Holdings"
                                className="w-max-content"
                                loader={downloadLoader}
                                onPress={_downloadHolidngs} />
                        </div>}
                    </div>
                </div>
                {
                    filterLoader === true || loader === true ?
                        <div className="h-80vh d-flex align-items-center justify-content-center">
                            <img src={require("../../../../Assets/Images/loader/page-loader.gif")}
                                alt={"loader"}
                                width={60}
                                height={60}
                            />
                        </div>
                        :
                        <Fragment>
                            {
                                portfolioType === 1 ?
                                    <div className="col-md-8 pb-4">
                                        <div className="e-box-shadow-white border-radius-24px pt-3 margin-32px-lr pb-3 e-portfolio-section d-flex flex-column">
                                            <div className="border-bottom-1px border-color-mint-gray d-sm-block d-none  padding-32px-lr">
                                                <CustomTabBar data={INVESTMENT_TYPES}
                                                    selectedTab={investmentType}
                                                    onSelectTab={_handleInvestmentType} />
                                            </div>
                                            {
                                                investmentType === 1 ?
                                                    <Fragment>
                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between border-bottom-1px border-color-mint-gray py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/total-returns.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                Total Returns
                                                            </div>
                                                            <span className={`${summaryData.total_investment.returns_amount > 0 ? 'color-emerald-green' : summaryData.total_investment.returns_amount < 0 ? 'color-deep-carmine-pink' : 'color-eerie-black'} e-font-20 e-line-height-24 e-montserrat-semi-bold`}>
                                                                {summaryData.total_investment.returns_amount_text ? summaryData.total_investment.returns_amount_text : '₹0'}
                                                                <span className={`${summaryData.total_investment.returns_percentage > 0 ? 'color-emerald-green' : summaryData.total_investment.returns_percentage < 0 ? 'color-deep-carmine-pink' : 'color-eerie-black'} e-font-16 letter-spacing-02em`}>({summaryData.total_investment.returns_percentage_text ? summaryData.total_investment.returns_percentage_text : '0%'})</span>
                                                            </span>
                                                        </div>

                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between  border-bottom-1px border-color-mint-gray py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/current.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                Current
                                                            </div>
                                                            <span className="color-eerie-black e-font-18 e-line-height-24 e-montserrat-semi-bold letter-spacing-02em">{summaryData.total_investment.current_amount_text ? summaryData.total_investment.current_amount_text : '₹0'}</span>
                                                        </div>

                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between   border-bottom-1px border-color-mint-gray py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/invested.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                Invested
                                                            </div>
                                                            <span className="color-eerie-black e-font-18 letter-spacing-02em e-line-height-24 e-montserrat-semi-bold">{summaryData.total_investment.invested_amount_text ? summaryData.total_investment.invested_amount_text : '₹0'}</span>
                                                        </div>

                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between border-bottom-1px border-color-mint-gray py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/xirr.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                XIRR
                                                            </div>
                                                            <span className={`${summaryData.total_investment.xirr > 0 ? "color-emerald-green" : summaryData.total_investment.xirr < 0 ? "color-deep-carmine-pink" : "color-eerie-black"} e-font-16 letter-spacing-02em e-line-height-24 e-montserrat-semi-bold d-flex align-items-center`}>
                                                                {summaryData.total_investment.xirr_text ? summaryData.total_investment.xirr_text : '₹0'}
                                                                {(summaryData.total_investment.xirr) &&
                                                                    <img width={16} alt="xirr" src={summaryData.total_investment.xirr > 0 ? require("../../../../Assets/Images/Home/xirr-high.svg").default : require("../../../../Assets/Images/Home/xirr-low.png")} />
                                                                }
                                                            </span>
                                                        </div>

                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/1-day-return.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                1 Day Return
                                                            </div>
                                                            <span className={`${summaryData.total_investment.one_day_returns > 0 ? "color-emerald-green" : summaryData.total_investment.one_day_returns < 0 ? "color-deep-carmine-pink" : "color-eerie-black"} e-font-18 letter-spacing-02em e-line-height-24 e-montserrat-semi-bold `}>{summaryData.total_investment.one_day_returns_text ? summaryData.total_investment.one_day_returns_text : 0}
                                                                <span className={`e-font-14 ${summaryData.total_investment.one_day_returns_percentage > 0 ? "color-emerald-green" : summaryData.total_investment.one_day_returns_percentage < 0 ? "color-deep-carmine-pink" : 'color-eerie-black'}`}>({summaryData.total_investment.one_day_returns_percentage_text ? summaryData.total_investment.one_day_returns_percentage_text : 0})</span>
                                                            </span>
                                                        </div>
                                                        {summaryData.updated_as_on && <div className="color-black e-font-14 e-line-height-16 e-montserrat-regular text-center mb-0 mt-auto mb-1"> Data as on {summaryData.updated_as_on}</div>}
                                                    </Fragment>
                                                    :
                                                    investmentType === 2 ?
                                                        <div className="pb-2">
                                                            <div className="py-3 border-bottom-1px border-color-mint-gray mb-sm-3 mb-1  padding-32px-lr">
                                                                <HorizontalStackedBar height="h-32px" allocations={categoryAllocationData || []} />
                                                            </div>

                                                            <div className="row  padding-32px-lr">
                                                                <div className="col-sm-6 col-5">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Category</span>
                                                                </div>
                                                                <div className="col-3 text-center">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Amount</span>
                                                                </div>
                                                                <div className="col-3 text-sm-center text-end">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Allocation(%)</span>
                                                                </div>
                                                            </div>
                                                            {
                                                                summaryData.categories.allocation_data !== undefined && summaryData.categories.allocation_data.map((item, index) => {
                                                                    return (
                                                                        <div className={`py-3 ${index > 0 ? 'border-top-point5px border-onyx' : ''}`}>
                                                                            <div className={`row align-items-center row-gap-12px  padding-32px-lr`}>
                                                                                <div className="col-sm-6 col-5 d-flex align-items-center gap-4px">
                                                                                    <span style={{ background: item.color }}
                                                                                        className="d-block rounded-circle w-10px h-10px "></span>
                                                                                    <p className="color-eerie-black e-font-14 e-line-height-20 e-montserrat-medium mb-0">{item.name}</p>
                                                                                </div>
                                                                                <div className="col-3 text-center">
                                                                                    <span className="color-jet-gray e-font-14 e-line-height-14 e-montserrat-bold">{item.current_amount_text}</span>
                                                                                </div>
                                                                                <div className="col-3 text-sm-center text-end">
                                                                                    <span className="color-jet-gray e-font-14 e-line-height-14 e-montserrat-bold">{item.allocation}%</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })
                                                            }

                                                        </div>

                                                        :
                                                        <div className="pb-2">
                                                            <div className="py-3 border-bottom-1px border-color-mint-gray mb-sm-3 mb-1  padding-32px-lr">
                                                                <HorizontalStackedBar height="h-32px" allocations={fundAllocationData || []} />
                                                            </div>

                                                            <div className="row  padding-32px-lr">
                                                                <div className="col-sm-6 col-5">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Fund name</span>
                                                                </div>
                                                                <div className="col-3 text-center">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Amount</span>
                                                                </div>
                                                                <div className="col-3 text-sm-center text-end">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Allocation(%)</span>
                                                                </div>
                                                            </div>
                                                            {
                                                                summaryData.funds.allocation_data !== undefined && summaryData.funds.allocation_data.map((item, index) => {

                                                                    return (
                                                                        <div className={`py-3 ${index > 0 ? 'border-top-point5px border-color-mint-gray' : ''}`}>
                                                                            <div className={`row row-gap-12px align-items-center  padding-32px-lr`}>
                                                                                <div className="col-sm-6 col-5 d-flex align-items-center gap-4px">
                                                                                    <span style={{ background: item.color }}
                                                                                        className="d-block rounded-circle w-10px h-10px "></span>
                                                                                    <p className="color-eerie-black e-font-14 e-line-height-20 e-montserrat-medium mb-0">{item.mf_name}</p>
                                                                                </div>
                                                                                <div className="col-3 text-center">
                                                                                    <span className="color-jet-gray e-font-14 e-line-height-14 e-montserrat-bold">{item.current_amount_text}</span>
                                                                                </div>
                                                                                <div className="col-3 text-sm-center text-end">
                                                                                    <span className="color-jet-gray e-font-14 e-line-height-14 e-montserrat-bold">{item.allocation}%</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })
                                                            }
                                                        </div>
                                            }
                                        </div>
                                    </div>
                                    :
                                    summaryData.external_portfolio_imported === true && portfolioType === 2 ? <div className="col-md-8 pb-4">
                                        <div className="e-box-shadow-white border-radius-24px pt-3 margin-32px-lr pb-3 e-portfolio-section d-flex flex-column">
                                            <div className="border-bottom-1px border-color-mint-gray d-sm-block d-none  padding-32px-lr">
                                                <CustomTabBar data={INVESTMENT_TYPES}
                                                    selectedTab={investmentType}
                                                    onSelectTab={_handleInvestmentType} />
                                            </div>
                                            {
                                                investmentType === 1 ?
                                                    <Fragment>
                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between border-bottom-1px border-color-mint-gray py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/total-returns.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                Total Returns
                                                            </div>
                                                            <span className={`${summaryData.total_investment.returns_amount > 0 ? 'color-emerald-green' : summaryData.total_investment.returns_amount < 0 ? 'color-deep-carmine-pink' : 'color-eerie-black'} e-font-20 e-line-height-24 e-montserrat-semi-bold`}>
                                                                {summaryData.total_investment.returns_amount_text ? summaryData.total_investment.returns_amount_text : '₹0'}
                                                                <span className={`${summaryData.total_investment.returns_percentage > 0 ? 'color-emerald-green' : summaryData.total_investment.returns_percentage < 0 ? 'color-deep-carmine-pink' : 'color-eerie-black'} e-font-16 letter-spacing-02em`}>({summaryData.total_investment.returns_percentage_text ? summaryData.total_investment.returns_percentage_text : '0%'})</span>
                                                            </span>
                                                        </div>

                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between  border-bottom-1px border-color-mint-gray py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/current.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                Current
                                                            </div>
                                                            <span className="color-eerie-black e-font-18 e-line-height-24 e-montserrat-semi-bold letter-spacing-02em">{summaryData.total_investment.current_amount_text ? summaryData.total_investment.current_amount_text : '₹0'}</span>
                                                        </div>

                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between   border-bottom-1px border-color-mint-gray py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/invested.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                Invested
                                                            </div>
                                                            <span className="color-eerie-black e-font-18 letter-spacing-02em e-line-height-24 e-montserrat-semi-bold">{summaryData.total_investment.invested_amount_text ? summaryData.total_investment.invested_amount_text : '₹0'}</span>
                                                        </div>

                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between border-bottom-1px border-color-mint-gray py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/xirr.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                XIRR
                                                            </div>
                                                            <span className={`${summaryData.total_investment.xirr > 0 ? "color-emerald-green" : summaryData.total_investment.xirr < 0 ? "color-deep-carmine-pink" : "color-eerie-black"} e-font-16 letter-spacing-02em e-line-height-24 e-montserrat-semi-bold d-flex align-items-center`}>
                                                                {summaryData.total_investment.xirr_text ? summaryData.total_investment.xirr_text : '₹0'}
                                                                {(summaryData.total_investment.xirr) &&
                                                                    <img width={16} alt="xirr" src={summaryData.total_investment.xirr > 0 ? require("../../../../Assets/Images/Home/xirr-high.svg").default : require("../../../../Assets/Images/Home/xirr-low.png")} />
                                                                }
                                                            </span>
                                                        </div>

                                                        <div className="padding-32px-lr d-flex align-items-center justify-content-between py-sm-3 py-2">
                                                            <div className="d-flex align-items-center gap-8px e-font-14 e-line-height-16 e-montserrat-medium color-jet-gray">
                                                                <img src={require('../../../../Assets/Images/Home/1-day-return.png')}
                                                                    alt="total-returns"
                                                                    width={28}
                                                                    draggable={false} />
                                                                1 Day Return
                                                            </div>
                                                            <span className={`${summaryData.total_investment.one_day_returns > 0 ? "color-emerald-green" : summaryData.total_investment.one_day_returns < 0 ? "color-deep-carmine-pink" : "color-eerie-black"} e-font-18 letter-spacing-02em e-line-height-24 e-montserrat-semi-bold `}>{summaryData.total_investment.one_day_returns_text ? summaryData.total_investment.one_day_returns_text : 0}
                                                                <span className={`e-font-14 ${summaryData.total_investment.one_day_returns_percentage > 0 ? "color-emerald-green" : summaryData.total_investment.one_day_returns_percentage < 0 ? "color-deep-carmine-pink" : 'color-eerie-black'}`}>({summaryData.total_investment.one_day_returns_percentage_text ? summaryData.total_investment.one_day_returns_percentage_text : 0})</span>
                                                            </span>
                                                        </div>
                                                        {summaryData.updated_as_on && <div className="color-black e-font-14 e-line-height-16 e-montserrat-regular text-center mb-0 mt-auto mb-1"> Data as on {summaryData.updated_as_on}</div>}
                                                    </Fragment>
                                                    :
                                                    investmentType === 2 ?
                                                        <div className="pb-2">
                                                            <div className="py-3 border-bottom-1px border-color-mint-gray mb-sm-3 mb-1  padding-32px-lr">
                                                                <HorizontalStackedBar height="h-32px" allocations={categoryAllocationData || []} />
                                                            </div>

                                                            <div className="row  padding-32px-lr">
                                                                <div className="col-sm-6 col-5">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Category</span>
                                                                </div>
                                                                <div className="col-3 text-center">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Amount</span>
                                                                </div>
                                                                <div className="col-3 text-sm-center text-end">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Allocation(%)</span>
                                                                </div>
                                                            </div>
                                                            {
                                                                summaryData.categories.allocation_data !== undefined && summaryData.categories.allocation_data.map((item, index) => {
                                                                    return (
                                                                        <div className={`py-3 ${index > 0 ? 'border-top-point5px border-onyx' : ''}`}>
                                                                            <div className={`row align-items-center row-gap-12px  padding-32px-lr`}>
                                                                                <div className="col-sm-6 col-5 d-flex align-items-center gap-4px">
                                                                                    <span style={{ background: item.color }}
                                                                                        className="d-block rounded-circle w-10px h-10px "></span>
                                                                                    <p className="color-eerie-black e-font-14 e-line-height-20 e-montserrat-medium mb-0">{item.name}</p>
                                                                                </div>
                                                                                <div className="col-3 text-center">
                                                                                    <span className="color-jet-gray e-font-14 e-line-height-14 e-montserrat-bold">{item.current_amount_text}</span>
                                                                                </div>
                                                                                <div className="col-3 text-sm-center text-end">
                                                                                    <span className="color-jet-gray e-font-14 e-line-height-14 e-montserrat-bold">{item.allocation}%</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })
                                                            }

                                                        </div>

                                                        :
                                                        <div className="pb-2">
                                                            <div className="py-3 border-bottom-1px border-color-mint-gray mb-sm-3 mb-1  padding-32px-lr">
                                                                <HorizontalStackedBar height="h-32px" allocations={fundAllocationData || []} />
                                                            </div>

                                                            <div className="row  padding-32px-lr">
                                                                <div className="col-sm-6 col-5">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Fund name</span>
                                                                </div>
                                                                <div className="col-3 text-center">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Amount</span>
                                                                </div>
                                                                <div className="col-3 text-sm-center text-end">
                                                                    <span className="color-black e-font-14 e-line-height-16 e-montserrat-semi-bold">Allocation(%)</span>
                                                                </div>
                                                            </div>
                                                            {
                                                                summaryData.funds.allocation_data !== undefined && summaryData.funds.allocation_data.map((item, index) => {

                                                                    return (
                                                                        <div className={`py-3 ${index > 0 ? 'border-top-point5px border-color-mint-gray' : ''}`}>
                                                                            <div className={`row row-gap-12px align-items-center  padding-32px-lr`}>
                                                                                <div className="col-sm-6 col-5 d-flex align-items-center gap-4px">
                                                                                    <span style={{ background: item.color }}
                                                                                        className="d-block rounded-circle w-10px h-10px "></span>
                                                                                    <p className="color-eerie-black e-font-14 e-line-height-20 e-montserrat-medium mb-0">{item.mf_name}</p>
                                                                                </div>
                                                                                <div className="col-3 text-center">
                                                                                    <span className="color-jet-gray e-font-14 e-line-height-14 e-montserrat-bold">{item.current_amount_text}</span>
                                                                                </div>
                                                                                <div className="col-3 text-sm-center text-end">
                                                                                    <span className="color-jet-gray e-font-14 e-line-height-14 e-montserrat-bold">{item.allocation}%</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })
                                                            }
                                                        </div>
                                            }
                                        </div>
                                    </div>
                                        :
                                        <div className="col-md-8 pb-4">
                                            <div className="e-box-shadow-white border-radius-24px pt-3 margin-32px-lr pb-3 e-portfolio-section d-flex flex-column">
                                                <EmptyScreen className="my-5"
                                                    title="External Investments not imported"
                                                    image={require("../../../../Assets/Images/loader/empty-funds.png")}
                                                    description="" />
                                            </div>
                                        </div>}
                            <div className="col-md-8">
                                {Object.keys(holdingsOverview).length > 0 && <div className="mt-sm-4 mt-3 e-box-shadow-white border-radius-24px margin-32px-lr py-sm-4 py-3 mb-4">

                                    <div className="px-sm-4 px-3 pb-sm-4 pb-3 border-bottom-1px border-color-mint-gray d-flex justify-content-between align-items-center">
                                        <h6 className="color-eerie-black e-font-16 e-line-height-20 e-montserrat-semi-bold margin-2px-tb">Holdings Overview</h6>

                                    </div>

                                    <div className="px-sm-4 px-3">
                                        {portfolioType === 1 && <div className="border-bottom-1px border-color-mint-gray pt-2 d-sm-block d-none">
                                            <CustomTabBar data={overviewOptions}
                                                selectedTab={overviewType}
                                                onSelectTab={_handleOverview}
                                            />
                                        </div>}

                                        {

                                            Object.keys(holdingsOverview).length > 0 &&
                                            <div className="pt-md-4 pt-3">
                                                <div className="row row-gap-8px align-items-start" ref={holdingsRef}>
                                                    <div className="col-xl-6 order-xl-1 order-2">
                                                        {
                                                            holdingType === 1 && portfolioType === 1 &&
                                                            <div className="d-sm-block d-none">
                                                                <h6 className="e-montserrat-medium e-font-14 e-line-height-20 color-eerie-black mb-0">Achieve</h6>
                                                                <p className={`color-emerald-green e-font-24  e-line-height-40 e-montserrat-semi-bold pt-md-2 pt-0 mb-md-3 mb-0`}>
                                                                    {holdingsOverview.achieve_amount ? holdingsOverview.achieve_amount : 0} <span className="e-font-14 color-eerie-black e-montserrat-semi-bold">by {holdingsOverview.tenure}</span>
                                                                </p>
                                                            </div>
                                                        }
                                                        {
                                                            holdingsOverview.allocation_data.slice(0, showAllHoldings ? holdingsOverview.allocation_data.length : 10).map((item, index) => {
                                                                return (
                                                                    <div key={index}
                                                                        className={`d-flex align-items-start e-font-14 e-line-height-22 e-montserrat-medium color-eerie-black mb-2 gap-4px `}>
                                                                        <div className="min-w-10px min-h-10px border-radius-100px" style={{ background: item.color }}></div>
                                                                        <span className="w-max-content">{item.name}</span> - <span className="e-montserrat-semi-bold">{item.allocation}%</span>
                                                                    </div>
                                                                )
                                                            })

                                                        }
                                                        {holdingsOverview.allocation_data.length > 10 &&
                                                            <div className="col-12 text-center"> <LinkButton label={showAllHoldings ? "Show less" : "View more"}
                                                                className="e-font-14 e-line-height-18 "
                                                                onPress={_handleShowOverview} />
                                                            </div>
                                                        }
                                                    </div>
                                                    <div className="col-xl-6 order-xl-2 order-1 d-flex justify-content-xl-start justify-content-lg-center justify-content-center flex-column ">
                                                        {
                                                            holdingType === 1 &&
                                                            <div className="d-sm-none d-block">
                                                                <h6 className="e-montserrat-medium e-font-14 e-line-height-20 color-eerie-black mb-0">Achieve</h6>
                                                                <p className={`color-emerald-green e-font-24  e-line-height-40 e-montserrat-semi-bold pt-md-3 pt-0 mb-md-3 mb-0`}>
                                                                    {holdingsOverview.achieve_amount ? holdingsOverview.achieve_amount : 0} <span className="e-font-14 color-eerie-black e-montserrat-semi-bold">by {holdingsOverview.tenure}</span>
                                                                </p>
                                                            </div>
                                                        }

                                                        <DoughnutChart data={graphData}
                                                            total={holdingsOverview.invested_amount} />
                                                    </div>
                                                </div>


                                            </div>
                                        }

                                    </div>
                                </div>}
                                {holdingsList.length > 0 && <div className="mt-sm-4 mt-3 margin-32px-lr py-sm-4 py-3">

                                    <div className="px-sm-4 px-3 mb-3 border-bottom-1px border-color-mint-gray d-flex justify-content-between align-items-center">
                                        <h6 className="color-eerie-black e-font-16 e-line-height-20 e-montserrat-semi-bold margin-2px-tb">Holdings</h6>
                                        {portfolioType === 1 && <div>
                                            <CustomTabBar data={holdingOptions}
                                                selectedTab={holdingType}
                                                onSelectTab={_handleHoldings}
                                            /></div>}
                                    </div>



                                    {holdingsList.map((row, index) => {
                                        return (
                                            <div className={`border-1px border-color-mint-gray border-radius-24px mb-3 px-3 py-3`}>
                                                <div className="row row-gap-8px">
                                                    <div className="col-12 mb-2">
                                                        <div className="d-flex align-items-center gap-8px">
                                                            <img src={row.icon}
                                                                alt={row.name}
                                                                width={48}
                                                                height={48}
                                                                draggable={false}
                                                                className="border-radius-12px object-fit-contain border-1px border-color-mint-gray bg-white d-sm-block d-none" />

                                                            <p className="mb-0 padding-2px-top e-montserrat-semi-bold e-font-14 e-line-height-20 color-eerie-black">{row.investment_type === 1 ? `Milestone : ${row.name}` : row.name}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-4">
                                                        <div className="d-block">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 margin-2px-bottom color-outer-space">Current</p>
                                                            <p className="e-montserrat-semi-bold e-font-14 e-line-height-20 color-eerie-black mb-0">{row.current_amount_text}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-4">
                                                        <div className="d-block text-center">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 margin-2px-bottom color-outer-space">XIRR</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 ${row.xirr > 0 ? "color-emerald-green" : row.xirr < 0 ? "color-deep-carmine-pink" : "color-eerie-black"}`}>{row.xirr_text}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-4">
                                                        <div className="d-block text-end">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 margin-2px-bottom color-outer-space">SIP</p>
                                                            <p className="e-montserrat-semi-bold e-font-14 e-line-height-20 color-eerie-black mb-0">{row.monthly_sip ? row.monthly_sip : "-"}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-4">
                                                        <div className="d-block ">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 margin-2px-bottom color-outer-space">Invested</p>
                                                            <p className="e-montserrat-semi-bold e-font-14 e-line-height-20 color-eerie-black mb-0">{row.invested_amount_text}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-4">
                                                        <div className="d-block text-center">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 margin-2px-bottom color-outer-space">Day Change</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 ${row.day_change > 0 ? "color-emerald-green" : row.day_change < 0 ? "color-deep-carmine-pink" : "color-eerie-black"}`}>{row.day_change_text}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-4">
                                                        <div className="d-block text-end">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 margin-2px-bottom color-outer-space">Returns</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 ${row.returns_amount > 0 ? 'color-green' : row.returns_amount < 0 ? 'color-deep-carmine-pink' : 'color-eerie-black'}`}>{row.returns_amount_text}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}

                                </div>}
                            </div>
                            {portfolioType === 2 && <Fragment>
                                <div className="row padding-32px-lr">
                                    <div className="col-lg-9">
                                        <CustomTabBar data={FILTER_OPTIONS}
                                            selectedTab={selected}
                                            onSelectTab={_handleFilter} />
                                    </div>
                                    <div className="col-lg-3 text-end">

                                    </div>
                                </div>
                                {
                                    listLoader === true ?
                                        <div className="h-80vh d-flex align-items-center justify-content-center">
                                            <img src={require("../../../../Assets/Images/loader/page-loader.gif")}
                                                alt={"loader"}
                                                width={60}
                                                height={60}
                                            />
                                        </div>
                                        :
                                        Object.keys(externalData).length === 0 ?
                                            <EmptyScreen className="my-5"
                                                title="No External Investments found"
                                                image={require("../../../../Assets/Images/loader/empty-funds.png")}
                                                description="" />
                                            :
                                            <div className="position-relative">
                                                <DataTableContainer data={externalData.data}
                                                    columns={HOLDINGS_COLUMNS}
                                                    customStyles={TABLE_STYLE}
                                                    selectableRows={false}
                                                    paginationComponentOptions={{ noRowsPerPage: true }}
                                                    defaultSortFieldId={1}
                                                    pagination={true}
                                                    paginationServer={true}
                                                    perPage={12}
                                                    defaultSortAsc={false}
                                                    onChangePage={_handlePageChange}
                                                    paginationTotalRows={externalData.total_count}
                                                    paginationDefaultPage={externalData.current_page}
                                                    selectRows={(allSelected, selectedCount, selectedRows) => {
                                                    }}
                                                />
                                                <span className="e-montserrat-regular e-font-14 e-line-height-18 padding-32px-lr position-absolute color-jett-black bottom-10px">Showing {externalData.record} of {externalData.total_count} entries</span>
                                            </div>
                                }
                            </Fragment>}
                        </Fragment>
                }
            </div >
        </Fragment >
    )
}

export default ExternalInvestments